import * as React from 'react';
import { Handle, Position, type NodeProps, type Node, useReactFlow } from '@xyflow/react';
import { LoaderCircle, Check, CircleX, Clock, Pause, Copy as CopyIcon, Search, Scissors, Copy, X, Maximize2, Minimize2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { cn } from '@shared/utils/cn';
import PaletteIcon from '@shared/ui/palette-icon';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';
import { useOutputInspector } from '@/context/output-inspector';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

type PinBase = { id: string; label?: string };
type Pins = {
  exec?: { in?: PinBase[]; out?: PinBase[] };
  data?: { in?: (PinBase & { type?: string })[]; out?: (PinBase & { type?: string })[] };
};
type NodeStatus = 'idle' | 'running' | 'waiting' | 'executed' | 'error';
type Data = {
  label?: string;
  pins?: Pins;
  icon?: React.ReactNode;
  templateKey?: string;
  status?: NodeStatus;
  startedAt?: number; // epoch ms
  finishedAt?: number; // epoch ms
  durationMs?: number; // explicit duration if known
  // Optional error info for failed state
  error?: { name?: string; message?: string; stack?: string } | string;
  errorMessage?: string;
  /** Optional free-form attributes edited in the right-side config panel */
  attrs?: unknown;
  /** Story/demo-only: presentational live preview bubble (no SSE/store yet) */
  liveDemo?: { text?: string; done?: boolean };
};

type CustomRFNode = Node<Data>;

const composeHandleId = (channel: 'exec' | 'data', io: 'in' | 'out', rawId: string) => `${channel}:${io}:${rawId}`;

// Presentational inline bubble used only when `data.liveDemo` is provided.
function LiveBubble({ text = '', done = false }: { text?: string; done?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [pulse, setPulse] = React.useState(false);
  const [tailing, setTailing] = React.useState(true);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = React.useState(false);
  const copiedTimerRef = React.useRef<number | null>(null);
  const [maximized, setMaximized] = React.useState(false);

  // Line-based sealing aligned to monospace bubble width (30ch => 30 columns)
  const COLS = 30;
  const [sealed, setSealed] = React.useState<string[]>([]);
  const [working, setWorking] = React.useState<string>('');
  const prevTextRef = React.useRef<string>('');

  const colLen = React.useCallback((s: string) => Array.from(s).length, []);
  const takeCols = React.useCallback((s: string, n: number) => Array.from(s).slice(0, n).join(''), []);
  const dropCols = React.useCallback((s: string, n: number) => Array.from(s).slice(n).join(''), []);
  const lastWhitespaceIndex = React.useCallback((s: string) => {
    const arr = Array.from(s);
    for (let i = arr.length - 1; i >= 0; i--) {
      if (/\s/.test(arr[i])) return i;
    }
    return -1;
  }, []);

  React.useEffect(() => {
    const full = String(text || '');
    if (full === prevTextRef.current) return;

    const parts = full.split('\n');
    const nextSealed: string[] = [];
    let nextWorking = '';

    const pushWorkingIfFull = () => {
      // Prefer wrapping at whitespace within COLS; fallback to hard wrap
      while (colLen(nextWorking) > COLS) {
        const seg = takeCols(nextWorking, COLS);
        const breakAt = lastWhitespaceIndex(seg);
        if (breakAt >= 0) {
          const line = takeCols(nextWorking, breakAt).replace(/\s+$/, '');
          nextSealed.push(line);
          nextWorking = dropCols(nextWorking, breakAt + 1);
        } else {
          nextSealed.push(seg);
          nextWorking = dropCols(nextWorking, colLen(seg));
        }
      }
      if (colLen(nextWorking) === COLS) {
        const breakAt = lastWhitespaceIndex(nextWorking);
        if (breakAt >= 0) {
          const line = takeCols(nextWorking, breakAt).replace(/\s+$/, '');
          nextSealed.push(line);
          nextWorking = dropCols(nextWorking, breakAt + 1);
        } else {
          nextSealed.push(nextWorking);
          nextWorking = '';
        }
      }
    };

    for (let i = 0; i < parts.length; i++) {
      const line = parts[i];
      let remaining = line;
      while (remaining.length) {
        const room = COLS - colLen(nextWorking);
        if (room <= 0) {
          nextSealed.push(nextWorking);
          nextWorking = '';
          continue;
        }
        const chunk = takeCols(remaining, room);
        nextWorking += chunk;
        remaining = dropCols(remaining, colLen(chunk));
        pushWorkingIfFull();
      }
      // explicit newline seals the current working line
      if (i < parts.length - 1) {
        nextSealed.push(nextWorking.replace(/\s+$/, ''));
        nextWorking = '';
      }
    }

    if (done && nextWorking) {
      nextSealed.push(nextWorking);
      nextWorking = '';
    }

    prevTextRef.current = full;
    setSealed(nextSealed);
    setWorking(nextWorking);
  }, [text, done, COLS, colLen, takeCols]);

  // Compact bubble: render a scrollable content that we translate smoothly
  const bubblePreRef = React.useRef<HTMLPreElement | null>(null);
  const [lineHeightPx, setLineHeightPx] = React.useState<number>(12);
  React.useLayoutEffect(() => {
    const el = bubblePreRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight) || 12;
    setLineHeightPx(lh);
  }, []);
  const MAX_RENDER_LINES = 60;
  const renderedLines = React.useMemo(() => sealed.slice(-MAX_RENDER_LINES), [sealed]);
  const offsetLines = Math.max(0, renderedLines.length - 3);
  // Trigger a one-shot pulse when new text arrives
  React.useEffect(() => {
    if (!text) return;
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 800);
    return () => clearTimeout(t);
  }, [text]);
  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch { }
  };
  React.useEffect(() => () => { if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current); }, []);
  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 8;
    setTailing(nearBottom);
  }, []);

  // Smooth scroll helper (ease in/out)
  const smoothScrollTo = React.useCallback((el: HTMLElement, target: number, duration = 240) => {
    const start = el.scrollTop;
    const change = target - start;
    const startTime = performance.now();
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const step = (now: number) => {
      const p = Math.min(1, (now - startTime) / duration);
      el.scrollTop = start + change * ease(p);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  // Popover: scroll with the raw text stream (no line algorithm here)
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!open || !tailing || !el) return;
    const to = setTimeout(() => {
      try { smoothScrollTo(el, el.scrollHeight, 200); } catch { }
    }, 60);
    return () => clearTimeout(to);
  }, [text, open, tailing, smoothScrollTo]);
  React.useEffect(() => {
    if (!open) return;
    // tail to bottom on first open
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      try {
        (el as any).scrollTo?.({ top: el.scrollHeight, behavior: 'smooth' });
      } catch { }
    });
  }, [open]);
  return (
    <div
      className={cn(
        'absolute left-1/2 -translate-x-1/2',
        'bottom-[-6px] translate-y-full', // sits just outside the node bottom
        'pointer-events-auto z-10'
      )}
      role="region"
      aria-live="polite"
    >
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setMaximized(false); }}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'group w-[30ch] cursor-pointer select-none',
              'rounded-md border bg-background/95 shadow-md hover:bg-muted/70',
              'px-2.5 py-1.5 text-left',
              'focus:outline-none focus:ring-2 focus:ring-primary/50',
              'transition-none'
            )}
            title={done ? 'Stream finished — click to read' : 'Streaming — click to read'}
            aria-label={done ? 'Finished' : 'Streaming'}
          >
            {/* Visuals only; do not capture pointer to avoid hover flicker */}
            <div className="flex items-center gap-2 pointer-events-none">
              <span className="inline-flex items-center">
                <span className="relative inline-flex items-center justify-center mr-0.5">
                  {!done && pulse ? (
                    <span className="absolute inline-flex h-2 w-2 rounded-full bg-amber-500/80 opacity-75 animate-[ping_0.8s_ease-out_1]" />
                  ) : null}
                  <span
                    className={cn(
                      'inline-block w-2 h-2 rounded-full',
                      done ? 'bg-emerald-500' : 'bg-amber-500'
                    )}
                  />
                </span>
              </span>
              <div
                className="text-[10px] font-mono text-muted-foreground overflow-hidden"
                style={{ lineHeight: '1.25em', height: '3.75em' }}
              >
                <pre
                  ref={bubblePreRef}
                  className="whitespace-pre-wrap break-words m-0"
                  style={{
                    transform: `translateY(-${offsetLines * lineHeightPx}px)`,
                    transition: 'transform 220ms ease-out',
                    willChange: 'transform',
                  }}
                >
                  {renderedLines.join('\n') || '…'}
                </pre>
              </div>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "p-0",
            maximized ? "w-[calc(100vw-32px)] h-[calc(100vh-32px)] max-w-none" : "w-[560px] max-w-[80vw]"
          )}
          style={maximized ? { top: 16, left: 16 } : undefined}
          draggable={!maximized}
        >
          <div className={cn(maximized ? "flex h-full flex-col" : undefined)}>
            <div
              className={cn(
                "border-b px-3 py-2 flex items-center justify-between select-none",
                maximized ? "cursor-default" : "cursor-move"
              )}
              data-popover-drag-handle
            >
              <div className="text-xs font-medium">Live stream</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => setMaximized((m) => !m)} title={maximized ? 'Minimize' : 'Maximize'} aria-label={maximized ? 'Minimize' : 'Maximize'}>
                  {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={onCopy} title={copied ? 'Copied' : 'Copy all'} aria-label={copied ? 'Copied' : 'Copy all'}>
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpen(false)} title="Close">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className={cn(maximized ? "flex-1 flex flex-col" : undefined)}>
              <div
                ref={scrollRef}
                className={cn(
                  maximized ? "flex-1 overflow-auto p-3" : "max-h-[50vh] overflow-auto p-3"
                )}
                onScroll={handleScroll}
              >
                <pre className="text-sm font-mono whitespace-pre-wrap break-words">{text || ''}</pre>
              </div>
              <div className="px-3 py-2 text-[10px] text-muted-foreground border-t">
                {done ? <span className="text-emerald-600">finished</span> : <span className="text-red-600">streaming…</span>}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function CustomNodeImpl({ id, data, selected }: NodeProps<CustomRFNode>) {
  const rf = useReactFlow();
  const { openFor, setOpen } = useOutputInspector();
  const pins = data?.pins;
  const label = data?.label ?? id;
  const rt = useRuntimeStore((s) => s.nodes[id]);
  const rtStatus = rt?.status;
  const runCount = rt?.runCount ?? 0;
  const status = (() => {
    const s = (rt?.status as any) as ('idle' | 'queued' | 'running' | 'waiting' | 'paused' | 'succeeded' | 'failed' | 'skipped' | undefined);
    if (s === 'failed') return 'error';
    if (s === 'succeeded') return 'executed';
    if (s === 'running') return 'running';
    if (s === 'waiting') return 'waiting';
    if (s === 'paused') return 'running'; // treat paused/suspended as running visual
    return 'idle';
  })() as NodeStatus;

  // compute duration based on props; live update if running
  const computeDuration = React.useCallback(() => {
    if (status === 'running') {
      if (rt?.startedAt) return Math.max(0, Date.now() - rt.startedAt);
      return rt?.durationMs ?? 0;
    }
    if (status === 'executed') {
      if (typeof rt?.durationMs === 'number') return rt.durationMs;
      if (rt?.startedAt && rt?.endedAt) return Math.max(0, rt.endedAt - rt.startedAt);
      return 0;
    }
    return 0;
  }, [status, rt?.startedAt, rt?.endedAt, rt?.durationMs]);
  const [elapsed, setElapsed] = React.useState<number>(computeDuration());
  React.useEffect(() => setElapsed(computeDuration()), [computeDuration]);
  React.useEffect(() => {
    if (status !== 'running') return;
    const tick = () => setElapsed(computeDuration());
    // update more frequently for sub-second durations; otherwise every 1s
    const initial = computeDuration();
    const period = initial < 1000 ? 100 : 1000;
    const t = setInterval(tick, period);
    return () => clearInterval(t);
  }, [status, computeDuration]);

  const formatDuration = (ms: number): string => {
    if (!ms || ms < 0) return '0 ms';
    if (ms < 1000) return `${Math.floor(ms)} ms`;
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
    if (ms < 60_000) return `${Math.round(ms / 1000)} s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return `${m}m ${String(s).padStart(2, '0')}s`;
  };


  const statusNode = (() => {
    if (rtStatus === 'paused') {
      return <Pause className="size-4 text-muted-foreground" aria-label="Paused" />;
    }
    switch (status) {
      case 'running':
        return <LoaderCircle className="size-4 animate-spin text-muted-foreground" aria-label="Running" />;
      case 'executed':
        return <Check className="size-4 text-emerald-600 dark:text-emerald-400" aria-label="Executed" />;
      case 'waiting':
        return <Clock className="size-4 text-muted-foreground" aria-label="Waiting" />;
      case 'error':
        return <CircleX className="size-4 text-red-600 dark:text-red-400" aria-label="Error" />;
      case 'idle':
      default:
        return null;
    }
  })();
  const errorMsg = (typeof (data as any)?.error === 'string' ? (data as any)?.error : (data as any)?.error?.message) || rt?.lastErrorMessage || (data as any)?.errorMessage;
  const statusTitle = rtStatus === 'paused'
    ? 'Paused'
    : status === 'running'
      ? 'In progress'
      : status === 'waiting'
        ? 'Waiting'
        : status === 'executed'
          ? 'Executed'
          : status === 'error'
            ? (errorMsg || 'Error')
            : 'Idle';

  // Actions
  const handleCopy = React.useCallback(async () => {
    try {
      const node = rf.getNode(id);
      if (!node) return;
      await navigator.clipboard?.writeText(JSON.stringify(node, null, 2));
      try {
        toast.success('Copied node JSON');
      } catch { }
    } catch { }
  }, [id, rf]);

  const handleInspect = React.useCallback(() => {
    try {
      if (openFor) {
        openFor(id);
      } else {
        setOpen(true);
      }
    } catch { }
  }, [id, openFor, setOpen]);

  const handleCut = React.useCallback(async () => {
    await handleCopy();
    try {
      rf.deleteElements({ nodes: [{ id }] } as any);
      try {
        toast.success('Cut node');
      } catch { }
    } catch { }
  }, [id, rf, handleCopy]);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            'rf-card relative overflow-visible rounded border border-foreground/30 px-3 py-2 min-w-[180px] bg-background',
            // Status-driven border color (only when not selected)
            !selected && status === 'running' && 'border-amber-500',
            !selected && status === 'waiting' && 'border-teal-500',
            !selected && status === 'executed' && 'border-emerald-500',
            !selected && status === 'error' && 'border-red-500',
            // Subtle status glow when not selected
            !selected && status === 'running' && 'ring-1 ring-amber-400/15',
            !selected && status === 'executed' && 'ring-1 ring-emerald-400/15',
            !selected && status === 'error' && 'ring-1 ring-red-400/15',
            // Selection outline kept subtle
            selected && 'ring-1 ring-blue-400',
          )}
          role="group"
          aria-label={label}
          style={{ ['--pad-x' as any]: '0.75rem', ['--node-border' as any]: '1px', ['--handle-size' as any]: '12px' }}
        >
          {/* LIVE stream bubble — prefers runtime stream; falls back to story/demo */}
          {(() => {
            const liveFromRt = (rt as any)?.stream as { text?: string; done?: boolean } | undefined;
            const hasRt = !!(liveFromRt && (liveFromRt.text || liveFromRt.done));
            const liveText = hasRt ? String(liveFromRt!.text || '') : String(data?.liveDemo?.text || '');
            const liveDone = hasRt ? !!liveFromRt!.done : !!data?.liveDemo?.done;
            return hasRt || data?.liveDemo ? <LiveBubble text={liveText} done={liveDone} /> : null;
          })()}
          <div className="flex items-center gap-2 text-sm font-medium truncate bg-muted/30 -mx-3 -mt-2 mb-2 px-3 py-1 min-h-[20px] border-b border-foreground/10" title={label}>
            {data?.icon ? (
              <span className="shrink-0 text-muted-foreground [&_svg]:!size-3" aria-hidden>
                {typeof data.icon === 'string' ? <PaletteIcon icon={data.icon} className="!size-3" /> : data.icon}
              </span>
            ) : null}
            <span className="truncate">{label}</span>
          </div>

          <div className="grid grid-cols-[auto_1fr_auto] items-start gap-x-2">
            {/* Left (In) */}
            <div className="flex flex-col gap-1">
              {/* Exec In */}
              {pins?.exec?.in?.map((p) => (
                <div key={`exec-in-${p.id}`} className="relative flex items-center h-4">
                  <Handle
                    id={composeHandleId('exec', 'in', p.id)}
                    type="target"
                    position={Position.Left}
                    className="rf-handle exec"
                    title={`Exec In: ${p.id}`}
                  />
                  <div className="ml-1 text-[10px] leading-none text-muted-foreground select-none" aria-hidden>
                    {p.label ?? p.id}
                  </div>
                </div>
              ))}
              {/* Data In */}
              {pins?.data?.in?.length ? <div className="h-1" /> : null}
              {pins?.data?.in?.map((p) => (
                <div key={`data-in-${p.id}`} className="relative flex items-center h-4">
                  <Handle
                    id={composeHandleId('data', 'in', p.id)}
                    type="target"
                    position={Position.Left}
                    className="rf-handle data"
                    title={`Data In: ${p.id}`}
                    data-coarse={p.type ?? 'any'}
                  />
                  <div className="ml-1 text-[10px] leading-none text-muted-foreground select-none" aria-hidden>
                    {p.label ?? p.id}
                  </div>
                </div>
              ))}
            </div>

            {/* Spacer/Body (optional center content) */}
            <div />

            {/* Right (Out) */}
            <div className="flex flex-col gap-1 items-end">
              {/* Exec Out */}
              {pins?.exec?.out?.map((p) => (
                <div key={`exec-out-${p.id}`} className="relative flex items-center h-4">
                  <div className="mr-1 text-[10px] leading-none text-muted-foreground select-none text-right" aria-hidden>
                    {p.label ?? p.id}
                  </div>
                  <Handle
                    id={composeHandleId('exec', 'out', p.id)}
                    type="source"
                    position={Position.Right}
                    className="rf-handle exec"
                    title={`Exec Out: ${p.id}`}
                  />
                </div>
              ))}
              {/* Data Out */}
              {pins?.data?.out?.length ? <div className="h-1" /> : null}
              {pins?.data?.out?.map((p) => (
                <div key={`data-out-${p.id}`} className="relative flex items-center h-4">
                  <div className="mr-1 text-[10px] leading-none text-muted-foreground select-none text-right" aria-hidden>
                    {p.label ?? p.id}
                  </div>
                  <Handle
                    id={composeHandleId('data', 'out', p.id)}
                    type="source"
                    position={Position.Right}
                    className="rf-handle data"
                    title={`Data Out: ${p.id}`}
                    data-coarse={p.type ?? 'any'}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer: status + duration */}
          <div className="mt-2 bg-muted/30 -mx-3 -mb-2 px-3 py-1 flex items-center justify-between text-[11px] leading-none border-t border-foreground/10">
            <span className="flex items-center justify-center min-w-4 min-h-4">
              {statusNode ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center justify-center">{statusNode}</span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{statusTitle}</TooltipContent>
                </Tooltip>
              ) : null}
            </span>
            <span className="text-muted-foreground tabular-nums font-mono text-[9px]">{(status === 'running' || status === 'executed') ? formatDuration(elapsed) : ''}</span>
          </div>

          {/* Run count pill (bottom-left) — show when executed more than once */}
          {runCount > 1 && (
            <div
              className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded border bg-background/80 text-muted-foreground shadow-sm cursor-default select-none"
              title={`Runs: ${runCount}`}
              aria-label={`Executed ${runCount} times`}
            >
              ×{runCount}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={handleCopy}>
          <CopyIcon />
          Copy
        </ContextMenuItem>
        <ContextMenuItem onSelect={handleInspect}>
          <Search />
          Inspect
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleCut}>
          <Scissors />
          Cut
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

const areEqual = (prev: NodeProps<CustomRFNode>, next: NodeProps<CustomRFNode>) => {
  if (prev.id !== next.id) return false;
  if (prev.selected !== next.selected) return false;

  // Cheap bailout
  if (prev.data === next.data) return true;

  const pd = (prev.data ?? {}) as Data;
  const nd = (next.data ?? {}) as Data;

  if (pd.label !== nd.label) return false;
  if (pd.status !== nd.status) return false;
  if ((pd as any).icon !== (nd as any).icon) return false;
  if (pd.startedAt !== nd.startedAt) return false;
  if (pd.finishedAt !== nd.finishedAt) return false;
  if (pd.durationMs !== nd.durationMs) return false;
  if ((pd as any).error !== (nd as any).error) return false;
  if ((pd as any).errorMessage !== (nd as any).errorMessage) return false;
  // Live demo bubble changes should re-render
  if ((pd as any).liveDemo?.text !== (nd as any).liveDemo?.text) return false;
  if ((pd as any).liveDemo?.done !== (nd as any).liveDemo?.done) return false;

  // Pins by ref-equality (good for perf)
  if (pd.pins !== nd.pins) return false;

  return true;
};

export default React.memo(CustomNodeImpl, areEqual);
